import React from 'react';
import i18next from 'i18next';
import { HotKeys } from 'react-hotkeys';
import { connect } from 'react-redux';
import InputRange from 'react-input-range';
import Chips from '../../Chips/Chips';
import PendingButton from '../../Button/PendingButton';
import apiForm from '../../Form/apiForm';
import Spinner from 'react-spinkit';
import Panel from '../../Panel/Panel';
import InputTextIcon from '../../InputTextIcon/InputTextIcon';
import ButtonBorder from '../../Button/ButtonBorder';
import FormErrors from '../../Form/FormErrors';
import { isNumeric, numberWithSpaces } from '../../../utils';
import {
  CRASH_STATUS_BETTING,
  CRASH_STATUS_DISCONNECTED,
  CRASH_STATUS_ENDED,
  CRASH_STATUS_IN_GAME,
  CRASH_STATUS_LOADING,
  ORDER_STATUS_AUTO_STOPPED,
  ORDER_STATUS_MANUAL_STOPPED,
} from '../CrashGame';
import Api from '../../../api';

import xIcon from 'icons/x.svg';
import coinIcon from 'icons/coin.svg';
import makeCancelable from '../../../api/cancelable';
import classNames from 'classnames';

const MIN_SUM = 0;
const MAX_SUM = 10000;

class CouponInfoBlock extends React.Component {
  shouldComponentUpdate(nextProps) {
    return this.props.stopped === false;
  }

  render() {
    const { closedAt, sum, currentCf, autoCloseAt, errors } = this.props;

    return (
      <div className="crash-coupon__stake-block">
        <div className="crash-coupon__win-block">
          <div
            className={
              'crash-coupon__win-amount' +
              (closedAt
                ? ' win'
                : status === CRASH_STATUS_ENDED
                ? ' loose'
                : '')
            }
          >
            {closedAt
              ? numberWithSpaces(Math.floor(sum * closedAt)) + ' R'
              : numberWithSpaces(
                  Math.floor(
                    sum * (status === CRASH_STATUS_ENDED ? 1 : currentCf)
                  )
                ) + ' R'}
          </div>

          {closedAt ? (
            <Chips
              value={Math.floor(sum * closedAt)}
              className="crash-coupon__chips"
              round={true}
            />
          ) : (
            <Chips
              value={Math.floor(sum * currentCf)}
              round={true}
              className={
                'crash-coupon__chips' +
                (status === CRASH_STATUS_ENDED ? ' fade-out' : '')
              }
            />
          )}
        </div>

        <div className="crash-coupon__stake-info">
          <div>
            {i18next.t('coupon.sum')}: <span>{sum && `${sum} R`}</span>
          </div>
          <div>
            {i18next.t('coupon.autoclose')}:{' '}
            <span>
              {(closedAt || autoCloseAt) && (closedAt ? closedAt : autoCloseAt)}
            </span>
          </div>
        </div>
        <FormErrors errors={errors} />
      </div>
    );
  }
}

class Coupon extends React.PureComponent {
  constructor(props) {
    super(props);

    this.stakeRef = React.createRef();
    this.autoCloseAtRef = React.createRef();

    this.state = {
      loaded: false,
      pending: false,
      placed: false,
      stopped: false,
      closedAt: null,
      sum: MIN_SUM,
      autoCloseAt: '',
    };

    this.requests = [];
  }

  componentWillUnmount() {
    this.requests.forEach((cancel) => cancel());
  }

  componentDidMount() {
    this.loadCurrentOrder();
  }

  componentDidUpdate(prevProps) {
    if (this.props.status !== prevProps.status) {
      this.props.clearErrors();

      if (this.props.status === CRASH_STATUS_BETTING) {
        this.setState({
          pending: false,
          placed: false,
          stopped: false,
          closedAt: null,
        });
      }
    }

    if (!this.state.stopped) {
      if (this.props.placedBet && this.props.placedBet.closed) {
        this.setState({
          stopped: true,
          closedAt: this.props.placedBet.cf,
        });
      }
    }
  }

  onSumChange = (e) => {
    this.updateSum(e.currentTarget.value, false);
  };

  onSumBlur = (e) => {
    this.updateSum(e.currentTarget.value);
  };

  onSumSliderChange = (value) => {
    this.updateSum(value);
  };

  onAutoCloseAtChange = (e) => {
    this.updateAutoCloseAt(e.currentTarget.value, false);
  };

  onAutoCloseAtBlur = (e) => {
    this.updateAutoCloseAt(e.currentTarget.value);
  };

  onKeyDown = (e) => {
    //Enter
    if (e.keyCode === 13) {
      if (this.props.status === CRASH_STATUS_BETTING) {
        this.onStakePlace();
      }
      if (this.props.status === CRASH_STATUS_IN_GAME) {
        this.onStakeStop();
      }
    }
  };

  onStakePlace = () => {
    const { sum, autoCloseAt } = this.state;

    this.setState({ placed: false });

    this.props.beforeSubmit();

    const { promise, cancel } = makeCancelable(Api.openOrder(sum, autoCloseAt));
    this.requests.push(cancel);

    promise.then((response) => {
      this.props.afterSubmit(response);

      if (response.status === 'success') {
        this.setState({ placed: true });
        this.props.updateBalance();
      } else {
        this.setState({ placed: false });
      }
    });
  };

  onStakeStop = () => {
    this.props.beforeSubmit();

    const { promise, cancel } = makeCancelable(Api.stopOrder());
    this.requests.push(cancel);

    promise.then((response) => {
      this.props.afterSubmit(response);

      if (response.status === 'success') {
        this.props.updateBalance();
      }
    });
  };

  loadCurrentOrder = () => {
    const { promise, cancel } = makeCancelable(Api.currentOrder());
    this.requests.push(cancel);

    promise.then((response) => {
      this.setState({ loaded: true });

      if (response.status === 'success') {
        if (response.order) {
          const { price, summ, status } = response.order;

          this.setState({ placed: true, sum: +summ, autoCloseAt: price });

          const stopped = [
            ORDER_STATUS_AUTO_STOPPED,
            ORDER_STATUS_MANUAL_STOPPED,
          ].includes(status);
          if (stopped) {
            this.setState({
              stopped,
              closedAt: +price,
            });
          }
        }
      }
    });
  };

  updateSum(value, validate = true) {
    if (validate) {
      if (!isNumeric(value)) value = MIN_SUM;
      value = Math.min(this.props.balance, MAX_SUM, Math.max(MIN_SUM, value));
    }

    this.setState({ sum: value });
  }

  updateAutoCloseAt(value) {
    if (value && !isNumeric(value)) value = '';

    this.setState({ autoCloseAt: value });
  }

  render() {
    const {
      onInputChange,
      formState: { pending, errors, invalid },
    } = this.props;
    const { currentCf, status, signedIn, balance } = this.props;
    const { loaded, stopped, placed, sum, autoCloseAt, closedAt } = this.state;
    const sliderValue = isNumeric(sum) ? +sum : MIN_SUM;
    const controlsDisabled = pending || (placed && !stopped);

    const handlers = {
      buttonAction: () => this.onKeyDown,
    };

    const map = {
      buttonAction: ['enter', 'space'],
    };

    if (!loaded || (signedIn && balance == null))
      return (
        <Panel
          title={i18next.t('crash.panel_coupon')}
          className="crash-coupon crash-coupon--empty"
        >
          <div className="crash-coupon__inner">
            <Spinner name="ball-clip-rotate" color="white" fadeIn="quarter" />
          </div>
        </Panel>
      );

    if (!signedIn)
      return (
        <Panel
          title={i18next.t('crash.panel_coupon')}
          className="crash-coupon crash-coupon--empty"
        >
          <div className="crash-coupon__inner">
            <div>{i18next.t('coupon.unauthorized')}</div>
          </div>
        </Panel>
      );

    return (
      <Panel
        title={i18next.t('crash.panel_coupon')}
        className="crash-coupon"
        onKeyDown={this.onKeyDown}
        tabIndex="0"
      >
        <HotKeys
          keyMap={map}
          handlers={handlers}
          className="crash-coupon__inner"
        >
          <span
            className={classNames(
              'status',
              [CRASH_STATUS_DISCONNECTED, CRASH_STATUS_LOADING].includes(status)
                ? 'offline'
                : 'online'
            )}
          />

          <div className="crash-coupon__controls-block">
            <div className="crash-coupon__sum">
              <span className="crash-coupon__label">
                {i18next.t('coupon.bet_title')}
              </span>
              <InputTextIcon
                onBlur={this.onSumBlur}
                onChange={this.onSumChange}
                value={sum}
                inputRef={this.stakeRef}
                className={invalid.includes('summ') && 'invalid'}
                disabled={controlsDisabled ? 'disabled' : null}
                glyph={coinIcon}
                maxLength={6}
              />
            </div>
            <div className="crash-coupon__autoclose">
              <span className="crash-coupon__label">
                {i18next.t('coupon.autoclose_title')}
              </span>
              <InputTextIcon
                onBlur={this.onAutoCloseAtBlur}
                onChange={this.onAutoCloseAtChange}
                value={autoCloseAt ? autoCloseAt : ''}
                inputRef={this.autoCloseAtRef}
                className={invalid.includes('price') && 'invalid'}
                disabled={controlsDisabled ? 'disabled' : null}
                glyph={xIcon}
                maxLength={6}
              />
            </div>
            <div className="crash-coupon__slider">
              <InputRange
                maxValue={Math.min(balance, MAX_SUM)}
                minValue={MIN_SUM}
                step={
                  sliderValue > 1000
                    ? 100
                    : sliderValue > 500
                    ? 50
                    : sliderValue > 100
                    ? 10
                    : sliderValue > 10
                    ? 5
                    : 1
                }
                value={Math.max(
                  MIN_SUM,
                  Math.min(balance, MAX_SUM, sliderValue)
                )}
                onChange={this.onSumSliderChange}
                disabled={controlsDisabled}
              />
            </div>
          </div>

          <div className="crash-coupon__bottom">
            {placed ? (
              <CouponInfoBlock
                closedAt={closedAt}
                sum={sum}
                currentCf={currentCf}
                autoCloseAt={autoCloseAt}
                errors={errors}
                pending={pending}
                stopped={stopped}
              />
            ) : (
              <pre className="crash-coupon__stake-block empty">
                {i18next.t('coupon.no_bets')}
              </pre>
            )}

            <div className="crash-coupon__button-block">
              {placed ? (
                !stopped ? (
                  <PendingButton
                    onClick={this.onStakeStop}
                    className="crash-coupon__stake-button"
                    disabled={
                      status !== CRASH_STATUS_IN_GAME || pending
                        ? 'disabled'
                        : null
                    }
                    tagName="button"
                    pending={pending}
                  >
                    <span>{i18next.t('coupon.stop')}</span>
                  </PendingButton>
                ) : (
                  <ButtonBorder
                    className="crash-coupon__stake-button"
                    disabled="disabled"
                  >
                    {i18next.t('coupon.bet_closed')}
                  </ButtonBorder>
                )
              ) : (
                <PendingButton
                  onClick={this.onStakePlace}
                  className="crash-coupon__stake-button"
                  disabled={
                    status !== CRASH_STATUS_BETTING ||
                    status === CRASH_STATUS_DISCONNECTED ||
                    pending
                      ? 'disabled'
                      : null
                  }
                  pending={pending}
                  tagName="button"
                >
                  {i18next.t('coupon.place')}
                </PendingButton>
              )}
            </div>
          </div>
        </HotKeys>
      </Panel>
    );
  }
}

export default connect((state) => {
  return {
    signedIn: !!state.user.profile,
    balance: state.user.balance,
  };
}, {})(apiForm(Coupon));



// WEBPACK FOOTER //
// ./app/components/CrashGame/Coupon/Coupon.jsx